import {
  Alert,
  Button,
  Drawer,
  EmptyState,
  Icon,
  List,
  Stack,
  Stepper,
} from '@hkdevs/design-system';
import { useState } from 'react';
import {
  acceptedCount,
  Col,
  FilePicker,
  plural,
  SentFilesTable,
  useSendFiles,
  type Sent,
} from './SentFiles';

/**
 * Upload files (pc-dbb.10): a right drawer in two steps. Step 1 takes one or more CSV or
 * Excel files; step 2 sends each one and shows each one's status as it's processed, with
 * the product types an upload newly registered (B11).
 */

export const UPLOAD_STEPS = ['Select files', 'Processing'] as const;

export interface UploadFilesDrawerProps {
  open: boolean;
  onClose: () => void;
  /** A rejected upload's error report. */
  onOpenErrorReport: (upload: { id: string; fileName: string }) => void;
}

/** The Upload files drawer. Remount it (a `key`) each time it opens, to start at step 1. */
export function UploadFilesDrawer({ open, onClose, onOpenErrorReport }: UploadFilesDrawerProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [sent, setSent] = useState<Sent[] | null>(null);
  const { send, busy } = useSendFiles();

  const step = sent ? 2 : 1;
  const accepted = sent ? acceptedCount(sent) : 0;
  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="right"
      size="md"
      title="Upload files"
      description={<Stepper steps={UPLOAD_STEPS} current={step} />}
      dismissable={!busy}
      footer={
        step === 1 ? (
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={() => void send(files, 'pay_history').then(setSent)}
              disabled={files.length === 0}
              loading={busy}
              loadingText="Uploading…"
            >
              Upload
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Done</Button>
        )
      }
    >
      {sent ? (
        <Stack gap={5}>
          <EmptyState
            icon={
              accepted > 0 ? (
                <Icon name="check-circle" tone="success" />
              ) : (
                <Icon name="alert-circle" tone="danger" />
              )
            }
            title={
              accepted === sent.length
                ? plural(accepted, 'file queued', 'files queued')
                : `${accepted} of ${plural(sent.length, 'file', 'files')} queued`
            }
            description="Processing continues in the background, and the list updates as each file finishes. You can close this panel and keep working."
            headingLevel={2}
          />
          <SentFilesTable sent={sent} onOpenErrorReport={onOpenErrorReport} />
        </Stack>
      ) : (
        <Stack gap={4}>
          <FilePicker files={files} onFilesChange={setFiles} disabled={busy} />
          <Alert tone="info" title="How files are read">
            <List>
              <li>
                Recognized columns: <Col>PAYEE NAME</Col>, <Col>PAYEE EMAIL</Col>,{' '}
                <Col>PRODUCT TYPE</Col>, <Col>PAY CATEGORY</Col>, <Col>CUSTOMER NAME</Col>,{' '}
                <Col>TOTAL</Col>, <Col>NOTE</Col>, <Col>PAY DATE</Col>. Other columns are kept with
                each line item.
              </li>
              <li>
                Rows are matched to users by <Col>PAYEE EMAIL</Col>.
              </li>
              <li>
                Each row’s <Col>PAY DATE</Col> sets its pay date, so one file may cover several.
              </li>
              <li>
                Uploading adds to the existing data for those pay dates; it doesn’t replace it.
              </li>
              <li>
                A file with any invalid row is rejected in full. Other files in the same upload
                still process.
              </li>
            </List>
          </Alert>
        </Stack>
      )}
    </Drawer>
  );
}
